import { useContext, useState } from "react";
import Button from "../../../atoms/button";
import { gradingTypes, gradingTypeSupportsAutograph, normalizeGradingType, preLacreFormatLabels, priorityTypes } from "../../../utils/gradingTypes";
import { cashnator, decashnator, isNumeric } from "../../../utils/functions";
import ToastContext from "../../../../context/toastProvider";
import { Field, inputClasses } from "../../../atoms/formField";

const ProductStep = ({ setStep, deliveryData, setProductData, productData, extendedArt = false }) => {

    const { badToast } = useContext(ToastContext);

    const [gradingType, setGradingType] = useState(productData?.gradingType || "premium");
    const [numberOfCards, setNumberOfCards] = useState(productData?.numberOfCards || "");
    const [priority, setPriority] = useState(productData?.priority || "low");
    const [declaredValue, setDeclaredValue] = useState(cashnator(productData?.declaredValue) || "R$0,00");
    const [autographEvaluation, setAutographEvaluation] = useState(!!productData?.autographEvaluation);
    const [preLacreFormat, setPreLacreFormat] = useState(productData?.preLacreFormat || "with_subgrades");
    const [priorityBumped, setPriorityBumped] = useState(false);

    // Arte estendida (spec §5.2): sem tipo de graduação; autógrafo sempre oferecido (D6, provisório).
    const supportsAutograph = extendedArt || gradingTypeSupportsAutograph(gradingType);

    const handleNumberOfCards = (value) => {
        if (value !== "" && !isNumeric(value)) {
            return null;
        }
        let numberValue = parseInt(value);
        setNumberOfCards(value);
        const before = priority;
        if (numberValue > 1200) {
            setNumberOfCards(1200);
            numberValue = 1200;
        }
        let next = priority;
        if (numberValue > 84 && ["none"].includes(next)) {
            next = "low";
        }
        if (numberValue > 126 && ["none", "low"].includes(next)) {
            next = "mid";
        }
        if (numberValue > 252 && ["none", "low", "mid"].includes(next)) {
            next = "high";
        }
        if (numberValue > 504 && ["none", "low", "mid", "high"].includes(next)) {
            next = "max";
        }
        if (next !== before) {
            setPriority(next);
            setPriorityBumped(true);
        }
    }

    const handleDeclaredValue = (value) => {
        if (!value) {
            setDeclaredValue("");
            return null;
        }
        const newValue = decashnator(value);
        if (isNumeric(newValue)) {
            setDeclaredValue(cashnator(newValue));
        }
    }

    const handleNext = () => {
        if (disableNext) {
            return null;
        }
        setProductData(pd => ({
            ...pd,
            ...(extendedArt ? {} : { gradingType }),
            numberOfCards,
            priority,
            declaredValue: decashnator(declaredValue),
            autographEvaluation: supportsAutograph && autographEvaluation,
            ...(!extendedArt && normalizeGradingType(gradingType) === "pre" ? { preLacreFormat } : {})
        }));
        if (deliveryData?.delivery === "presencial") {
            setStep(4);
            return null;
        }
        if (deliveryData?.delivery === "correios") {
            setStep(3);
            return null;
        }
        badToast("Algo deu errado, tente novamente");
    }

    const goBack = () => {
        setStep(1);
    }

    const declaredTooLow = declaredValue !== "" && decashnator(declaredValue) < 100;
    const disableNext = (!extendedArt && gradingType === "") || (!numberOfCards || numberOfCards === "0") || !priority || decashnator(declaredValue) < 100;

    return (
        <div className="relative">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {extendedArt ? null : (
                    <Field label="Tipo de graduação" required>
                        <select
                            value={gradingType}
                            onChange={e => {
                                const next = e.target.value;
                                setGradingType(next);
                                if (!gradingTypeSupportsAutograph(next)) {
                                    setAutographEvaluation(false);
                                }
                            }}
                            className={inputClasses}
                        >
                            {
                                Object.keys(gradingTypes).map((g, i) => (
                                    <option key={i} value={g}>
                                        {gradingTypes[g]}
                                    </option>
                                ))
                            }
                        </select>
                        {/* Aviso fixo (independe do plano): PREMIUM é o único sem autógrafo. */}
                        <span className="text-xs text-amber-400/90">
                            Autenticação de assinatura (avaliação de autógrafo) não está disponível no Manafix PREMIUM, só nos outros planos.
                        </span>
                    </Field>
                )}

                <Field label="Número de cartas" required hint="Máximo de 1200 por pedido">
                    <input
                        className={inputClasses}
                        type="text"
                        inputMode="numeric"
                        placeholder="Ex.: 25"
                        value={numberOfCards}
                        onChange={e => handleNumberOfCards(e.target.value)}
                    />
                </Field>

                {
                    !extendedArt && normalizeGradingType(gradingType) === "pre" ?
                        <Field label="Formato do pré lacre">
                            <select value={preLacreFormat} onChange={e => setPreLacreFormat(e.target.value)} className={inputClasses}>
                                {
                                    Object.keys(preLacreFormatLabels).map((k, i) => (
                                        <option key={i} value={k}>{preLacreFormatLabels[k]}</option>
                                    ))
                                }
                            </select>
                        </Field>
                        : null
                }

                <Field
                    label="Prioridade"
                    required
                    hint={priorityBumped ? "Ajustada automaticamente pelo volume de cartas" : "Volumes altos exigem prioridade mínima"}
                >
                    <select value={priority} onChange={e => { setPriority(e.target.value); setPriorityBumped(false); }} className={inputClasses}>
                        {
                            Object.keys(priorityTypes).map((p, i) => (
                                <option key={i} value={p}>
                                    {priorityTypes[p]}
                                </option>
                            ))
                        }
                    </select>
                </Field>

                <Field label="Valor declarado dos itens" required hint={declaredTooLow ? undefined : "Base do seguro e da taxa de manuseio"}>
                    <input
                        className={`${inputClasses} ${declaredTooLow ? "border-red-500" : ""}`}
                        type="text"
                        inputMode="numeric"
                        value={declaredValue}
                        onChange={e => handleDeclaredValue(e.target.value)}
                    />
                    {declaredTooLow ? <span className="text-xs text-red-500">Informe pelo menos R$ 1,00</span> : null}
                </Field>

                {supportsAutograph ? (
                    <label className="flex items-center gap-2 self-end pb-1 text-left text-sm text-laranjinha/80 sm:col-span-2">
                        <input
                            type="checkbox"
                            checked={autographEvaluation}
                            onChange={e => setAutographEvaluation(e.target.checked)}
                            className="h-4 w-4 accent-[#d59106]"
                        />
                        Incluir avaliação de autógrafo
                    </label>
                ) : null}
            </div>

            <div className="m-auto mt-5 flex w-fit gap-3">
                <Button click={goBack} classes="bg-purple-400 hover:bg-purple-300 px-4">← Voltar</Button>
                <Button click={handleNext} classes="bg-green-400 hover:bg-green-300 px-6" disabled={disableNext}>Avançar →</Button>
            </div>
        </div>
    )
}

export default ProductStep;
